/**
 * SEOHelmet Component
 * 
 * A wrapper around react-helmet-async for consistent SEO across pages.
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SEO, generatePersonSchema, generateBlogPostSchema } from '../SEO';
import { useLanguage } from '../i18n';

const SEOHelmet = ({
  title,
  description,
  pathname = '',
  image,
  type = 'website',
  article = null,
  noindex = false,
}) => {
  const siteUrl = SEO.siteUrl;
  const { language, t } = useLanguage();
  const url = `${siteUrl}${pathname}`;

  // Map app languages to OG locales
  const localeMap = {
    en: 'en_CA',
    pt: 'pt_BR',
    fr: 'fr_FR'
  };

  const metaTitle = title ? `${title} | ${SEO.siteName}` : t('seo.defaultTitle');
  const metaDescription = description || t('seo.defaultDescription');
  const metaImage = image ? `${siteUrl}${image}` : `${siteUrl}${SEO.defaultImage}`;

  // Generate structured data
  const structuredData = article
    ? generateBlogPostSchema(article)
    : generatePersonSchema();

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={SEO.keywords.join(', ')} />
      <meta name="author" content={SEO.siteName} />
      <meta name="robots" content={noindex ? 'noindex,nofollow' : 'index,follow'} />
      <meta name="googlebot" content={noindex ? 'noindex,nofollow' : 'index,follow'} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={url} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:site_name" content={SEO.siteName} />
      <meta property="og:locale" content={localeMap[language] || 'en_CA'} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />
      <meta name="twitter:creator" content={SEO.twitterHandle} />
      <meta name="twitter:site" content={SEO.twitterHandle} />
      
      {/* Article-specific meta tags */}
      {article && (
        <>
          <meta property="article:author" content={article.author} />
          <meta property="article:published_time" content={article.date instanceof Date ? article.date.toISOString() : article.date} />
          <meta property="article:modified_time" content={article.date instanceof Date ? article.date.toISOString() : article.date} />
          {article.tags?.map(tag => (
            <meta property="article:tag" content={tag} key={tag} />
          ))}
        </>
      )}
      
      {/* Structured Data (JSON-LD) */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

export default SEOHelmet;
